import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiUserPlus, FiBriefcase, FiCalendar, FiMapPin, FiPhone } from '../utils/icons';
import { getAllEmployees } from '../services/adminService';
import { Employee } from '../types';
import { EmployeeWizardComplete } from '../components/employee/EmployeeWizardComplete';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
  padding: 24px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 16px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 12px;
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: -8px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-top: 0;
  }
`;

const PageTitle = styled.h1`
  font-size: ${({ theme }) => theme.fontSizes.xxxl};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  margin: 0;
  margin-left: 56px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-left: 64px;
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background-color: ${({ theme }) => theme.colors.secondary};
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }

  svg {
    font-size: 18px;
  }
`;

const ContentCard = styled.div`
  background-color: ${({ theme }) => theme.colors.surface};
  padding: ${({ theme }) => theme.spacing.xl};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const StatCard = styled.div<{ color: string }>`
  background: ${({ color }) => color};
  padding: ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  color: white;
  box-shadow: ${({ theme }) => theme.shadows.md};
`;

const StatLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  opacity: 0.9;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const StatValue = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xxxl};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
`;

const OnboardingTable = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background-color: ${({ theme }) => theme.colors.surfaceLight};
`;

const Th = styled.th`
  padding: ${({ theme }) => theme.spacing.md};
  text-align: left;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text.secondary};
  border-bottom: 2px solid ${({ theme }) => theme.colors.divider};
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
  transition: background-color ${({ theme }) => theme.transitions.fast};

  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceLight};
  }
`;

const Td = styled.td`
  padding: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  text-transform: uppercase;
  letter-spacing: 0.5px;

  ${({ $status, theme }) => {
    switch ($status) {
      case 'Pending':
        return `
          background-color: #fef3c7;
          color: #92400e;
        `;
      case 'In Progress':
        return `
          background-color: #dbeafe;
          color: #1e40af;
        `;
      case 'Completed':
        return `
          background-color: #d1fae5;
          color: #065f46;
        `;
      default:
        return `
          background-color: ${theme.colors.surfaceLight};
          color: ${theme.colors.text.secondary};
        `;
    }
  }}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xxl};
  color: ${({ theme }) => theme.colors.text.secondary};

  svg {
    font-size: 64px;
    margin-bottom: ${({ theme }) => theme.spacing.md};
    opacity: 0.5;
  }

  h3 {
    font-size: ${({ theme }) => theme.fontSizes.xl};
    margin-bottom: ${({ theme }) => theme.spacing.sm};
  }

  p {
    font-size: ${({ theme }) => theme.fontSizes.md};
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 15px;
`;

const ErrorMessage = styled.div`
  padding: 16px 20px;
  background-color: ${({ theme }) => theme.colors.surfaceLight};
  color: ${({ theme }) => theme.colors.error};
  border-radius: 12px;
  border-left: 4px solid ${({ theme }) => theme.colors.error};
  font-size: 14px;
`;

export const OnboardingPage: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isWizardOpen, setIsWizardOpen] = useState(false);

  useEffect(() => {
    loadEmployees();
  }, []);

  const loadEmployees = async () => {
    try {
      setLoading(true);

      // Fetch employees with embedded organization and location names
      const employeesResponse = await getAllEmployees(undefined, { page: 1, limit: 100 });

      if (employeesResponse.success && employeesResponse.data) {
        setEmployees(employeesResponse.data);
        setError(null);
      } else {
        setError('Failed to load employees');
      }
    } catch (err) {
      setError('An error occurred while loading employees');
      console.error('Employee loading error:', err);
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    { label: 'Total Onboarding', value: employees.length, color: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
    { label: 'Pending', value: employees.filter(e => e.status === 'Pending').length, color: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
    { label: 'In Progress', value: employees.filter(e => e.status === 'In Progress').length, color: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
    { label: 'Completed', value: employees.filter(e => e.status === 'Completed').length, color: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' }
  ];

  const handleAddEmployee = () => {
    setIsWizardOpen(true);
  };

  const handleWizardClose = () => {
    setIsWizardOpen(false);
  };

  const handleWizardSuccess = () => {
    setIsWizardOpen(false);
    loadEmployees(); // Reload the employees list
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingMessage>Loading employees...</LoadingMessage>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle>Employee Onboarding</PageTitle>
        </PageHeader>
        <ErrorMessage>{error}</ErrorMessage>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Employee Onboarding</PageTitle>
        <AddButton onClick={handleAddEmployee}>
          <FiUserPlus />
          Add New Employee
        </AddButton>
      </PageHeader>

      <StatsGrid>
        {stats.map((stat, index) => (
          <StatCard key={index} color={stat.color}>
            <StatLabel>{stat.label}</StatLabel>
            <StatValue>{stat.value}</StatValue>
          </StatCard>
        ))}
      </StatsGrid>

      <ContentCard>
        {employees.length > 0 ? (
          <OnboardingTable>
            <Table>
              <Thead>
                <tr>
                  <Th>Employee</Th>
                  <Th>Contact</Th>
                  <Th>Position</Th>
                  <Th>Organization</Th>
                  <Th>Location</Th>
                  <Th>Start Date</Th>
                  <Th>Status</Th>
                </tr>
              </Thead>
              <Tbody>
                {employees.map((employee) => (
                  <Tr key={employee._id}>
                    <Td>
                      <strong>{employee.name}</strong>
                    </Td>
                    <Td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {employee.mobileNumber && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                            <FiPhone style={{ fontSize: '12px' }} />
                            {employee.mobileNumber}
                          </div>
                        )}
                        {employee.employeeId && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                            ID: {employee.employeeId}
                          </div>
                        )}
                      </div>
                    </Td>
                    <Td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <FiBriefcase style={{ fontSize: '14px' }} />
                        {employee.designation || 'N/A'}
                      </div>
                    </Td>
                    <Td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <FiBriefcase style={{ fontSize: '12px', color: '#666' }} />
                        <strong>{employee.orgName || `Org ID: ${employee.orgId}`}</strong>
                      </div>
                    </Td>
                    <Td>
                      {employee.locationNames && employee.locationNames.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {employee.locationNames.map((location, idx) => (
                            <div key={location.locationId} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                              <FiMapPin style={{ fontSize: '12px', color: '#888' }} />
                              <span>{location.name || `Location ID: ${location.locationId}`}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: '#999', fontSize: '13px' }}>No location</span>
                      )}
                    </Td>
                    <Td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <FiCalendar style={{ fontSize: '14px' }} />
                        {employee.createdAt ? new Date(employee.createdAt).toLocaleDateString() : 'N/A'}
                      </div>
                    </Td>
                    <Td>
                      <StatusBadge $status={employee.status}>
                        {employee.status}
                      </StatusBadge>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </OnboardingTable>
        ) : (
          <EmptyState>
            <FiUserPlus />
            <h3>No Employees Onboarding</h3>
            <p>Click "Add New Employee" to start the onboarding process</p>
          </EmptyState>
        )}
      </ContentCard>

      <EmployeeWizardComplete
        isOpen={isWizardOpen}
        onClose={handleWizardClose}
        onSuccess={handleWizardSuccess}
      />
    </PageContainer>
  );
};
